import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import { FiChevronLeft, FiChevronRight, FiLogOut, FiMenu } from "react-icons/fi";
import farmsNavIcon from "../../assets/icons/admin-nav-farms.svg";
import guestUsersNavIcon from "../../assets/icons/admin-nav-guest.svg";
import marketingNavIcon from "../../assets/icons/admin-nav-marketing.svg";
// import operationsNavIcon from "../../assets/icons/admin-nav-operations.svg";
import ordersNavIcon from "../../assets/icons/admin-nav-orders.svg";
import overallNavIcon from "../../assets/icons/admin-nav-overall.svg";
import registeredUsersNavIcon from "../../assets/icons/admin-nav-registered.svg";
import broadcastNavIcon from "../../assets/icons/admin-push-notification.svg";
import AgrogridBrandLogo from "../../assets/images/agrogrid-brand.png";
import AgrogridBrandLogoSmall from "../../assets/images/agrogrid-logo.png";
import { ADMIN_PERMISSIONS, ADMIN_ROLES, getDefaultAdminPath, useAdminAuth } from "../../providers/adminAuthentication.provider";
import { AdminDrawerGlobalStyle, AdminLayoutStyle } from "./admin.layout.style";

const navGroups = [
  {
    label: "Dashboards",
    items: [
      { label: "Operations", to: "/admin/internal/dashboard/operations", icon: overallNavIcon },
      { label: "Marketing", to: "/admin/internal/dashboard/marketing", icon: marketingNavIcon },
    ],
  },
  {
    label: "Operations",
    items: [
      { label: "Order by Split-Buys", to: "/admin/internal/operations/orders", icon: ordersNavIcon, roles: ADMIN_PERMISSIONS.OPERATIONS },
      { label: "Order by users", to: "/admin/internal/operations/orders/users", icon: registeredUsersNavIcon, roles: ADMIN_PERMISSIONS.OPERATIONS },
      { label: "Order & Ratings Overview", to: "/admin/internal/operations/orders/ratings", icon: ordersNavIcon, roles: ADMIN_PERMISSIONS.OPERATIONS },
      { label: "Market prices", to: "/admin/internal/operations/market-prices", icon: farmsNavIcon, roles: ADMIN_PERMISSIONS.OPERATIONS },
    ],
  },
  {
    label: "Marketing",
    items: [
      { label: "Registered Users", to: "/admin/internal/customers/registered", icon: registeredUsersNavIcon, roles: ADMIN_PERMISSIONS.CUSTOMERS },
      { label: "Guest Users", to: "/admin/internal/customers/guests", icon: guestUsersNavIcon, roles: ADMIN_PERMISSIONS.CUSTOMERS },
      { label: "Farms", to: "/admin/internal/customers/farms", icon: farmsNavIcon, roles: ADMIN_PERMISSIONS.CUSTOMERS },
      { label: "Broadcast", to: "/admin/internal/marketing/broadcast", icon: broadcastNavIcon, roles: ADMIN_PERMISSIONS.MARKETING },
    ],
  },
];

const getPageTitle = (pathname) => {
  if (pathname.includes("/operations/orders/ratings")) return "Order & Ratings Overview";
  if (/\/operations\/orders\/[^/]+$/.test(pathname)) return "Transaction details";
  if (/\/resources\/split-buy\/[^/]+$/.test(pathname)) return "Split-Buy details";
  if (pathname.includes("/operations/orders/users")) return "Order by users";
  if (pathname.includes("/operations/orders")) return "Order by Split-Buys";
  if (pathname.includes("/resources/split-buy")) return "Split-Buys";
  if (/\/admin\/internal\/(buyers|customers\/(registered|guests))\/[^/]+$/.test(pathname)) return "User details";
  if (pathname.includes("/customers/registered")) return "Registered Users";
  if (pathname.includes("/customers/guests")) return "Guest Users";
  if (pathname.includes("/customers/farms")) return "Farms";
  if (pathname === "/admin/internal/buyers" || pathname.includes("/marketing/buyers")) return "Registered Users";
  if (pathname.includes("/marketing/broadcast/history")) return "Broadcast history";
  if (pathname.includes("/marketing/broadcast") || pathname.includes("/marketing/communications")) return "Broadcast";
  if (pathname.includes("/operations/market-prices/history")) return "Market price history";
  if (pathname.includes("/operations/market-prices") || pathname.includes("/operations/mk-prices")) return "Market Prices";
  if (pathname.includes("/dashboard/operations")) return "Operations dashboard";
  if (pathname.includes("/dashboard/marketing")) return "Marketing dashboard";
  return "Admin portal";
};

function Navigation({ isCollapsed = false, onNavigate, role }) {
  const isOpsAdmin = role === ADMIN_ROLES.OPS;

  return (
    <nav className="admin-navigation" aria-label="Admin navigation">
      {navGroups.map((group) => {
        if (isOpsAdmin && group.label === "Marketing") return null;

        const availableItems = group.items.filter((item) => {
          if (isOpsAdmin && (item.to.includes("/marketing/") || item.to.includes("/customers/"))) return false;
          return !item.roles || item.roles.includes(role);
        });
        if (!availableItems.length) return null;

        return (
          <section className="nav-group" key={group.label} aria-labelledby={`nav-${group.label.toLowerCase()}`}>
            {!isCollapsed && <h2 id={`nav-${group.label.toLowerCase()}`} className="nav-group-label">{group.label}</h2>}
            <div className="nav-items">
              {availableItems.map(({ label, to, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/admin/internal/operations/orders"}
                  title={isCollapsed ? label : undefined}
                  onClick={onNavigate}
                  className={({ isActive }) => `nav-item ${isCollapsed ? "collapsed" : ""} ${isActive ? "active" : ""}`}
                >
                  <span className="nav-icon"><img src={icon} alt="" /></span>
                  {!isCollapsed && <span className="nav-label">{label}</span>}
                </NavLink>
              ))}
            </div>
          </section>
        );
      })}
    </nav>
  );
}

function SidebarContent({ isCollapsed = false, onNavigate, role, user, logout }) {
  const navigate = useNavigate();
  const displayName = user?.firstname || user?.firstName || "Admin";
  const lastName = user?.lastname || user?.lastName || "";
  const roleLabel = role?.replaceAll("_", " ");
  const initials = `${displayName[0] || "A"}${lastName[0] || displayName[1] || "G"}`.toUpperCase();

  return (
    <>
      <Link className={`admin-brand ${isCollapsed ? "collapsed" : ""}`} to={getDefaultAdminPath(role)} onClick={onNavigate} aria-label="Agrogrid admin home">
        <img src={isCollapsed ? AgrogridBrandLogoSmall : AgrogridBrandLogo} alt="Agrogrid" />
      </Link>

      <Navigation isCollapsed={isCollapsed} onNavigate={onNavigate} role={role} />

      <div className={`sidebar-account ${isCollapsed ? "collapsed" : ""}`}>
        <div className="account-avatar" aria-hidden="true">{initials}</div>
        {!isCollapsed && <div className="account-copy"><strong>{displayName} {lastName}</strong><span>{roleLabel}</span></div>}
        <button
          type="button"
          className="logout-button"
          onClick={() => {
            logout();
            onNavigate?.();
            navigate("/admin/internal/login", { replace: true });
          }}
          aria-label="Log out of admin portal"
          title="Log out"
        >
          <FiLogOut aria-hidden="true" />
        </button>
      </div>
    </>
  );
}

export const AdminLayout = () => {
  const location = useLocation();
  const { user, role, logout } = useAdminAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const closeDrawer = () => setIsDrawerOpen(false);
  const displayName = user?.firstname || user?.firstName || "Admin";
  const lastName = user?.lastname || user?.lastName || "";
  const initials = `${displayName[0] || "A"}${lastName[0] || displayName[1] || "G"}`.toUpperCase();

  return (
    <>
      <AdminDrawerGlobalStyle />
      <AdminLayoutStyle>
      <div className="admin-shell">
        <aside className={`desktop-sidebar ${isCollapsed ? "collapsed" : ""}`}>
          <button
            type="button"
            className="collapse-button"
            onClick={() => setIsCollapsed((current) => !current)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
          <SidebarContent isCollapsed={isCollapsed} role={role} user={user} logout={logout} />
        </aside>

        <div className="admin-workspace">
          <header className="admin-topbar">
            <div className="topbar-title">
              <button type="button" className="mobile-menu-button" onClick={() => setIsDrawerOpen(true)} aria-label="Open admin navigation"><FiMenu /></button>
              <div><span>Administration</span><h1>{getPageTitle(location.pathname)}</h1></div>
            </div>
            <div className="topbar-actions">
              <div className="topbar-profile"><div className="account-avatar">{initials}</div><div><strong>{displayName} {lastName}</strong><span>{role?.replaceAll("_", " ")}</span></div></div>
            </div>
          </header>

          <main className="admin-content" id="admin-main-content"><Outlet /></main>
        </div>

        <Offcanvas isOpen={isDrawerOpen} toggle={closeDrawer} direction="start" className="admin-mobile-drawer">
          <OffcanvasHeader toggle={closeDrawer} className="admin-mobile-drawer-header">
            <img src={AgrogridBrandLogo} alt="Agrogrid" />
          </OffcanvasHeader>
          <OffcanvasBody className="admin-mobile-drawer-body">
            <SidebarContent role={role} user={user} logout={logout} onNavigate={closeDrawer} />
          </OffcanvasBody>
        </Offcanvas>
      </div>
      </AdminLayoutStyle>
    </>
  );
};
